import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Alert, IconButton
} from '@mui/material';
import PersonIcon    from '@mui/icons-material/Person';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HomeIcon      from '@mui/icons-material/Home';
import CloseIcon     from '@mui/icons-material/Close';
import Api from './Api';
import { useAppTheme } from '../../contexts/ThemeContext';

const AssignPaymentModal = ({ open, onClose, transactionData, onConfirm }) => {
  const { palette, Colors } = useAppTheme();
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [tenantData, setTenantData] = useState(null);

  useEffect(() => { if (open && transactionData) fetchTenantByPhone(); }, [open, transactionData]);

  const fetchTenantByPhone = async () => {
    if (!transactionData?.phone_number) return;
    setLoading(true); setError(null);
    try {
      let phone = transactionData.phone_number;
      if (phone.startsWith('0')) phone = '254' + phone.substring(1);
      const data = await Api.getTenantByPhone(phone);
      // Backend now returns 200 with found:false when no tenant exists
      if (!data?.id || data?.found === false) {
        setError('No tenant found with this phone number. You can edit the transaction to manually assign it to a tenant.');
        setTenantData(null);
      } else {
        setTenantData(data);
      }
    } catch (err) {
      setError('No tenant found with this phone number. You can edit the transaction to manually assign it to a tenant.');
    }
    finally { setLoading(false); }
  };

  const handleConfirm = async () => {
    if (!tenantData || !transactionData) return;
    try {
      await onConfirm({ transaction_id: transactionData.transaction_id, tenant_id: tenantData.id });
      onClose();
    } catch (err) { setError(`Failed to confirm payment: ${err.message}`); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { backgroundColor: palette.background.secondary, color: palette.text.primary } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${Colors.primary.dark}, ${Colors.primary.main})`,
        color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <Typography variant="h6">Assign Payment to Tenant</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Transaction details */}
        <Box sx={{ backgroundColor: palette.background.tertiary, p: 2, borderRadius: 1, mb: 3, border: `1px solid ${palette.border}` }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: palette.text.primary }}>Transaction Details</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[['M-Pesa Ref', transactionData?.transaction_id], ['Amount', `KES ${transactionData?.amount?.toFixed(2)}`],
              ['Phone', transactionData?.phone_number], ['Date', transactionData?.transaction_time]].map(([label, val]) => (
              <Box key={label}>
                <Typography variant="body2" sx={{ color: palette.text.secondary }}>{label}:</Typography>
                <Typography variant="body1" sx={{ color: palette.text.primary }}>{val}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: palette.text.primary }}>Tenant Information</Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}><CircularProgress size={40} sx={{ color: Colors.primary.main }} /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : tenantData ? (
          <Box sx={{ border: `1px solid ${palette.border}`, borderRadius: 1, p: 2, backgroundColor: palette.background.tertiary }}>
            {[[<PersonIcon sx={{ color: Colors.primary.main, mr: 1 }} />, 'Name', tenantData.name],
              [<ApartmentIcon sx={{ color: Colors.primary.main, mr: 1 }} />, 'Apartment', tenantData.propertyName],
              [<HomeIcon sx={{ color: Colors.primary.main, mr: 1 }} />, 'Unit', tenantData.houseNumber]].map(([icon, label, val]) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {icon}
                <Typography variant="body1" sx={{ color: palette.text.primary }}>
                  <strong>{label}:</strong> {val}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Alert severity="info">No tenant information available</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: palette.text.secondary }}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading || !tenantData}
          sx={{ backgroundColor: Colors.primary.main, '&:hover': { backgroundColor: Colors.primary.dark } }}>
          Confirm Payment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignPaymentModal;